import React, { useState } from 'react';
import { useMood } from '../../contexts/MoodContext';
import { getMotionPref, setMotionPref } from './visual/motion';

/**
 * MoodSettings — the user's controls over the whole feature.
 * Prompts, history and skip-on-open are plain toggles; history and journal can be
 * cleared outright. Turning history off does not delete what is stored — a
 * separate "Clear" does that — and the basic check-in keeps working regardless.
 *
 * When the backend is enabled, this is also where a user explicitly imports the
 * mood history that was saved on this device before sync existed. It is opt-in and
 * idempotent — nothing is uploaded automatically.
 */
const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string; desc: string }> = ({
  checked,
  onChange,
  label,
  desc,
}) => (
  <label className="mm-toggle">
    <span className="mm-toggle__text">
      <span className="mm-toggle__label">{label}</span>
      <span className="mm-toggle__desc">{desc}</span>
    </span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={`mm-switch ${checked ? 'is-on' : ''}`}
      onClick={() => onChange(!checked)}
    >
      <span className="mm-switch__thumb" />
    </button>
  </label>
);

export const MoodSettings: React.FC = () => {
  const {
    preferences,
    updatePreferences,
    clearHistory,
    clearReflections,
    checkIns,
    reflections,
    status,
    canImportLocalHistory,
    localHistoryCount,
    importLocalHistory,
  } = useMood();

  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const [reduceMotion, setReduceMotion] = useState(() => getMotionPref() === 'reduce');

  const runImport = async () => {
    setImporting(true);
    setImportMsg(null);
    try {
      const r = await importLocalHistory();
      const parts = [`${r.imported} imported`];
      if (r.skipped) parts.push(`${r.skipped} already synced`);
      if (r.failed) parts.push(`${r.failed} failed`);
      setImportMsg(parts.join(' · '));
    } catch {
      setImportMsg('Import failed. Your on-device history is unchanged — you can try again.');
    } finally {
      setImporting(false);
    }
  };

  // Honest storage description: it differs by mode.
  const historyDesc = status.backend
    ? 'Keep private check-ins so your weekly and monthly journals work. Synced to your account so they follow you across devices.'
    : 'Keep private, on-device check-ins so your weekly and monthly journals work. Stored only in this browser.';

  return (
    <div className="mm-settings">
      {status.offline && (
        <p className="mm-settings__status" role="status">
          You’re offline. MoodMate is showing what’s saved on this device and will sync when you reconnect.
        </p>
      )}

      <Toggle
        checked={preferences.promptsEnabled}
        onChange={(v) => updatePreferences({ promptsEnabled: v })}
        label="Mood check-in on open"
        desc="Ask how you’re feeling once per session when you sign in."
      />
      <Toggle
        checked={preferences.saveHistory}
        onChange={(v) => updatePreferences({ saveHistory: v })}
        label="Save mood history & journal"
        desc={historyDesc}
      />
      <Toggle
        checked={preferences.skipOnOpen}
        onChange={(v) => updatePreferences({ skipOnOpen: v })}
        label="Don’t interrupt on open"
        desc="Never show the check-in automatically — you’ll open MoodMate yourself."
      />
      <Toggle
        checked={reduceMotion}
        onChange={(v) => {
          setReduceMotion(v);
          setMotionPref(v ? 'reduce' : 'full');
        }}
        label="Reduce animations"
        desc="Turn off the moving background, floating particles and illustration motion. The graphics stay — they just hold still."
      />

      {canImportLocalHistory && (
        <div className="mm-settings__import">
          <div className="mm-settings__import-text">
            <span className="mm-toggle__label">Import on-device history</span>
            <span className="mm-toggle__desc">
              You have {localHistoryCount} record{localHistoryCount === 1 ? '' : 's'} saved in this browser from before
              sync. Import them to your account — nothing is uploaded until you choose to.
            </span>
          </div>
          <button type="button" className="mm-btn mm-btn--ghost" onClick={runImport} disabled={importing}>
            {importing ? 'Importing…' : 'Import'}
          </button>
        </div>
      )}
      {importMsg && <p className="mm-settings__status" role="status">{importMsg}</p>}

      <div className="mm-settings__data">
        <button
          type="button"
          className="mm-btn mm-btn--ghost"
          onClick={clearHistory}
          disabled={checkIns.length === 0}
        >
          Clear mood history ({checkIns.length})
        </button>
        <button
          type="button"
          className="mm-btn mm-btn--ghost"
          onClick={clearReflections}
          disabled={reflections.length === 0}
        >
          Clear reflections ({reflections.length})
        </button>
      </div>
      <p className="mm-settings__note">
        {status.backend
          ? 'MoodMate data is private to your account, visible only to you, and never shared.'
          : 'All MoodMate data is stored only in this browser, never sent to a server, and never shared.'}
      </p>
    </div>
  );
};

export default MoodSettings;
